import React from 'react'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaTiktok,
  FaTwitter,
  FaHeadset
} from 'react-icons/fa'

const AuthLayout = () => {
  const location = useLocation()
  const isLoginPage = location.pathname === '/login'
  const isRegisterPage = location.pathname === '/register'

  const pageTitle = location.pathname === '/login' ? 'Log In'
    : location.pathname === '/register' ? 'Create Account'
      : location.pathname === '/forgot-password' ? 'Forgot Password'
        : location.pathname === '/reset-password' ? 'Reset Password'
          : 'Verify Email'

  const getLinkPath = (item) => {
    const linkMap = {
      'Help Centre': '/faq',
      'Payment Methods': '/payment-methods',
      'Order Tracking': 'https://www.flashexpress.ph/fle/tracking',
      'Free Shipping': '/shipping-policy',
      'Return & Refund': '/return-policy',
      'WiseCart Guarantee': '/guarantee',
      'Contact Us': '/contact',
      'About Us': '/about',
      'WiseCart Blog': '/blog',
      'Policies': '/policies',
      'WiseCart Privacy Policy': '/privacy-policy',
      'Flash Deals': '/flash-deals',
      'Contact': '/contact',
      'Terms of Service': '/terms-conditions',
      'Privacy Policy': '/privacy-policy',
      'Security': '/security',
      'Site Map': '/sitemap'
    }
    return linkMap[item] || '#'
  }

  const isExternalLink = (url) => url.startsWith('http')

  return (
    <div className="min-h-screen flex flex-col bg-[#ff5400]">

      {/* ── Header ── */}
      <div className="bg-white shadow-sm flex-shrink-0 z-20 relative border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-[84px]">
            <div className="flex items-center gap-3 sm:gap-8 min-w-0">
              <Link
                to="/"
                className="text-lg sm:text-xl md:text-2xl font-extrabold text-[#ff6900] tracking-tight shrink-0 hover:text-[#ff5400] transition-colors"
                style={{ fontFamily: "'EB Garamond', serif" }}
              >
                WISECART
              </Link>
              <span className="text-gray-700 font-medium text-sm sm:text-base truncate">{pageTitle}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <Link
                to="/contact"
                className="flex items-center gap-1.5 text-[#ff6900] hover:text-[#ff5400] transition-colors"
              >
                <FaHeadset className="text-base sm:text-xl text-[#ff6900] shrink-0" />
                <span className="hidden sm:block text-sm font-medium text-[#ff6900]">Need help?</span>
              </Link>
              {isRegisterPage && (
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-white bg-[#ff6900] rounded-lg hover:bg-[#ff5400] font-semibold transition-colors text-sm whitespace-nowrap shadow-md"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content area ── */}
      <div className="flex-1 flex items-start justify-center">

        {/* Decorative image — only on large screens */}
        <div
          className="hidden lg:block sticky top-0 self-start overflow-hidden flex-shrink-0 -mr-60 z-0"
          style={{ height: '600px' }}
        >
          <img
            src="/loginimage.png"
            alt="WISECART"
            className="w-full h-full object-contain object-center"
          />
        </div>

        {/* Form column — responsive width */}
        <div className="flex items-center justify-center w-full lg:w-[500px] lg:flex-shrink-0 px-4 sm:px-6 py-6 sm:py-10">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md bg-white rounded-xl shadow-2xl p-5 sm:p-8 relative z-10"
          >
            {isLoginPage && (
              <div className="text-center mb-5 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Welcome to WiseCart</h2>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">Sign in to continue shopping</p>
              </div>
            )}
            <Outlet />
          </motion.div>
        </div>

      </div>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">

            {/* CUSTOMER SERVICE */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-lg">CUSTOMER SERVICE</h3>
              <ul className="space-y-1.5 sm:space-y-2">
                {['Help Centre', 'Payment Methods', 'Order Tracking', 'Free Shipping', 'Return & Refund', 'WiseCart Guarantee', 'Contact Us'].map(item => {
                  const linkPath = getLinkPath(item)
                  return (
                    <li key={item}>
                      {isExternalLink(linkPath) ? (
                        <a href={linkPath} target="_blank" rel="noopener noreferrer"
                          className="text-gray-600 hover:text-[#ff6900] text-xs sm:text-sm transition-colors">
                          {item}
                        </a>
                      ) : (
                        <Link to={linkPath}
                          className="text-gray-600 hover:text-[#ff6900] text-xs sm:text-sm transition-colors">
                          {item}
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>

            {/* ABOUT WISECART */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-lg">ABOUT WISECART</h3>
              <ul className="space-y-1.5 sm:space-y-2">
                {['About Us', 'WiseCart Blog', 'Policies', 'WiseCart Privacy Policy', 'Flash Deals', 'Contact'].map(item => {
                  const linkPath = getLinkPath(item)
                  return (
                    <li key={item}>
                      {isExternalLink(linkPath) ? (
                        <a href={linkPath} target="_blank" rel="noopener noreferrer"
                          className="text-gray-600 hover:text-[#ff6900] text-xs sm:text-sm transition-colors">
                          {item}
                        </a>
                      ) : (
                        <Link to={linkPath}
                          className="text-gray-600 hover:text-[#ff6900] text-xs sm:text-sm transition-colors">
                          {item}
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>

            {/* PAYMENT & LOGISTICS */}
            <div>
              <div className="mb-5 sm:mb-8">
                <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-lg">PAYMENT</h3>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2 w-fit">
                  {[
                    { title: 'Visa', imgSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSrWXGDW65mRC-ef6y4SIDWOg7pUPJrlVmNqA&s', bg: 'bg-[#1734cd]', link: '#' },
                    { title: 'Mastercard', imgSrc: 'https://www.investopedia.com/thmb/F8CKM3YkF1fmnRCU2g4knuK0eDY=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/MClogo-c823e495c5cf455c89ddfb0e17fc7978.jpg', bg: 'bg-[#f0efeb]', link: '#' },
                    { title: 'GCash', imgSrc: 'https://img.utdstc.com/icon/711/4cb/7114cb1d21a6384a13ea739687e23c1faa7c131954b8d39d6da308cde9cdc04c:200', bg: 'bg-[#007DFF]', link: '#' },
                    { title: 'Maya', imgSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR5ojdFl553msQALwR7u4mA8cVREPsMVHyRqg&s', bg: 'bg-[#000000]', link: '#' },
                    { title: 'QRPh', imgSrc: 'https://123finance.ph/qrphImages/1729209697.png', bg: 'bg-[#ffffff]', link: '#' },
                    { title: 'ShopeePay', imgSrc: 'https://www.moneymax.ph/hs-fs/hubfs/279562802_659176625392175_8530308473468348344_n.jpg?width=2048&height=2048&name=279562802_659176625392175_8530308473468348344_n.jpg', bg: 'bg-[#EE4D2D]', link: '#' },
                  ].map(({ title, imgSrc, bg, link }) => (
                    <a key={title} href={link} target="_blank" rel="noopener noreferrer"
                      className={`${bg} p-1 h-8 w-12 sm:h-10 sm:w-14 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity`}>
                      <img src={imgSrc} alt={title} className="max-h-6 sm:max-h-8 max-w-full object-contain" />
                    </a>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-lg">LOGISTICS</h3>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2 w-fit">
                  {[
                    { title: 'Flash Express', imgSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTinL6Tk2I91g52zOEMlVkGVAT4NvLzl1lg2Q&s', bg: 'bg-[#faee1e]', link: 'https://www.flashexpress.ph' },
                  ].map(({ title, imgSrc, bg, link }) => (
                    <a key={title} href={link} target="_blank" rel="noopener noreferrer"
                      className={`${bg} p-1 h-8 w-12 sm:h-10 sm:w-14 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity`}>
                      <img src={imgSrc} alt={title} className="max-h-6 sm:max-h-8 max-w-full object-contain" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* FOLLOW US */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-lg">FOLLOW US</h3>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2 w-fit">
                {[
                  { icon: <FaFacebook className="text-xl text-gray-700 group-hover:text-white transition-colors" />, link: 'https://facebook.com/wisecart' },
                  { icon: <FaInstagram className="text-xl text-gray-700 group-hover:text-white transition-colors" />, link: 'https://instagram.com/wisecart' },
                  { icon: <FaTiktok className="text-xl text-gray-700 group-hover:text-white transition-colors" />, link: 'https://tiktok.com/@wisecart' },
                  { icon: <FaTwitter className="text-xl text-gray-700 group-hover:text-white transition-colors" />, link: 'https://twitter.com/wisecart' },
                  { icon: <FaYoutube className="text-xl text-gray-700 group-hover:text-white transition-colors" />, link: 'https://youtube.com/wisecart' },
                  { icon: <FaLinkedin className="text-xl text-gray-700 group-hover:text-white transition-colors" />, link: 'https://linkedin.com/company/wisecart' },
                ].map(({ icon, link }, i) => (
                  <a key={i} href={link} target="_blank" rel="noopener noreferrer"
                    className="bg-gray-100 p-1 h-8 w-12 sm:h-10 sm:w-14 rounded-lg flex items-center justify-center hover:bg-[#ff6900] transition-colors group">
                    <span className="group-hover:scale-110 transition-transform">{icon}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-200 mt-6 pt-4">
            <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-gray-500 gap-3">
              <div>© 2026 WISECART. All Rights Reserved.</div>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {['Terms of Service', 'Privacy Policy', 'Security', 'Site Map'].map(item => (
                  <Link key={item} to={getLinkPath(item)}
                    className="text-gray-500 hover:text-[#ff6900] transition-colors">
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default AuthLayout